import { useMediaQuery } from '@crehana/react-hooks';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { Container } from '@crehana/ui';

import BreadcrumbTracks from '../../components/BreadcrumbTracks';
import DetailLearnTrackHeader from '../../components/DetailLearnTrackHeader';
import ErrorViewDetail from '../../components/ErrorViewDetail';
import LearnTrackCourses from '../../components/LearnTrackCourses';
import { NewCoursesBanner } from '../../components/new-courses-banner';
import SkeletonLearnContentNew from '../../components/SkeletonLearnTeamTracks/SkeletonLearnContentNew';
import useGetDetailTrack from '../../hooks/useGetDetailTrack';
import useTeamCoursesTranslation from '../../hooks/useTeamCoursesTranslation/useTeamCoursesTranslation';
import {
  STATUS,
  type TDetailReward,
  type TDetailTrackBody,
  type THeaderDetail,
} from '../../types';
import { parseTrackExpirationDate } from '../../utils';
import { getNewlyUpdatedCourses } from '../../utils/get-newly-updated-courses';
import useToggleType from '../../utils/getTypeStateTrack';
import { isRouteClosed, isTrackExpired } from '../../utils/is-route-closed';

const DetailLearnTracks = () => {
  const { data, loading, error } = useGetDetailTrack();

  const { matches: isXs } = useMediaQuery({ only: 'xs' });
  const { languageCode } = useTeamCoursesTranslation();

  const parsedDate = parseTrackExpirationDate(data?.trackEndDate, languageCode);

  const teamExpired = isTrackExpired(parsedDate);
  const isClosed = Boolean(data?.isClosed);
  const routeClosed = isRouteClosed(isClosed, parsedDate);
  const newCoursesCount = getNewlyUpdatedCourses(
    data?.listCourses || [],
    data?.contentUpdatedAt || null,
  ).length;

  const detailTrackHeader: THeaderDetail = {
    trackName: data?.trackName || '',
    isTrackReward: Boolean(
      data?.trackReward.rewardId &&
        data?.trackReward.userRewardId &&
        data?.trackReward.status !== STATUS.REWARD_STATUS_INACTIVE,
    ),
    description: data?.trackDescription,
    assigned: '',
    coinPlayroomNumber: data?.trackPlayroomCoin,
    date: data?.trackEndDate,
    typeTrack: useToggleType(data),
    teamExpired,
    isClosed,
    contentUpdatedAt: data?.contentUpdatedAt || null,
    closedAt: data?.closedAt || null,
  };

  const detailTrackBody: TDetailTrackBody = {
    teamId: data?.trackId || 0,
    nameTrack: data?.trackName || '',
    mandatoryProjectsCount: data?.trackSummary.mandatoryProjectsCount || 0,
    mandatoryCoursesCount: data?.trackSummary.mandatoryCoursesCount || 0,
    mandatoryQuizzesCount: data?.trackSummary.mandatoryQuizzesCount || 0,
    shouldRenderRewardCard: Boolean(
      data?.trackReward.rewardId && data?.trackReward.userRewardId,
    ),
    trackDisabled: data?.trackDisabled || false,
    isCompletedTrack: data?.isCompletedTrack || false,
    inProgressMandatoryProjectsCount:
      data?.trackSummary.inProgressMandatoryProjectsCount || 0,
    inProgressCourses: data?.trackSummary.inProgressCourses || 0,
    inProgressQuizzesCount: data?.trackSummary.inProgressQuizzesCount || 0,
    dataCertificate: data?.dataCertificate || null,
    trackProjects: data?.lastProject || null,
    listCourses: data?.listCourses || [],
    coinPlayroomNumber: data?.trackPlayroomCoin,
    isCertificateGenerated: data?.hasCertificate || false,
    trackVersion: data?.trackVersion || null,
    contentUpdatedAt: data?.contentUpdatedAt || null,
    prereqsUpdatedAt: data?.prereqsUpdatedAt || null,
    prereqBannerType: data?.prereqBannerType || null,
    isOpenModality: Boolean(data?.isOpenModality),
    isRouteClosed: routeClosed,
    newCoursesCount,
  };

  const detailRewardBody: TDetailReward = {
    status: data?.trackReward.status || STATUS.REWARD_STATUS_INACTIVE,
    numberCourses: data?.trackReward.numberCourses || 0,
    rewardId: data?.trackReward?.userRewardId || 0,
  };

  const renderCoursesContent = () => {
    // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
    if (loading) return <SkeletonLearnContentNew />;

    // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
    if (!loading && !data) return <ErrorViewDetail />;

    // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
    if (!loading && error) return <ErrorViewDetail />;

    return (
      <div className="mb-112 tw-flex tw-flex-col tw-h-full">
        <div className="tw-mt-2 tw-pb-1.6">
          <DetailLearnTrackHeader detailHeaderTrack={detailTrackHeader} />
        </div>
        <NewCoursesBanner
          contentUpdatedAt={detailTrackBody.contentUpdatedAt}
          prereqsUpdatedAt={detailTrackBody.prereqsUpdatedAt}
          prereqBannerType={detailTrackBody.prereqBannerType}
          isCompletedTrack={detailTrackBody.isCompletedTrack}
        />
        <div className="tw-rounded-t-1 tw-h-full">
          {isXs ? (
            <section className="tw-h-full">
              <LearnTrackCourses
                detailTrack={detailTrackBody}
                detailReward={detailRewardBody}
              />
            </section>
          ) : (
            <Container type="dashboard" className="tw-h-full">
              <LearnTrackCourses
                detailTrack={detailTrackBody}
                detailReward={detailRewardBody}
              />
            </Container>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <BreadcrumbTracks
        className="tw-pt-2.4 tw-pb-1.2"
        isOnDetailTrack
        detailTrack={{
          teamId: detailTrackBody.teamId,
          trackName: detailTrackHeader.trackName,
        }}
      />
      <section
        data-testid="b2b-activity-team-courses"
        id="b2b-activity-team-courses"
        className="tw-h-full"
      >
        {renderCoursesContent()}
      </section>
    </>
  );
};

export default DetailLearnTracks;
